/**
 * In-memory object URLs for launcher icon bytes.
 * Nothing here is written to documents, localStorage, or logs.
 */
import { fetchLauncherIconBlob } from './uploadLauncherIcon';
import { readDealerAdminSession } from '../../pages/Admin/dealerAdminSession';

const entries = new Map();
const listeners = new Set();
let generation = 0;
let contextToken = null;

export function launcherIconContextToken(tenantId = '') {
  const dealer = readDealerAdminSession();
  return `${tenantId || ''}|${dealer?.dealerId || ''}|${dealer?.tenantId || ''}`;
}

function notify() {
  listeners.forEach(listener => {
    try {
      listener();
    } catch {
      /* a closed view must not keep private images alive */
    }
  });
}

export function subscribeLauncherIconCache(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function launcherIconGeneration() {
  return generation;
}

export function clearLauncherIconCache() {
  generation += 1;
  entries.forEach(entry => {
    if (entry.url && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(entry.url);
    }
  });
  entries.clear();
  notify();
}

export function resetLauncherIconSession() {
  contextToken = null;
  clearLauncherIconCache();
}

export function syncLauncherIconContext(token) {
  if (contextToken === null) {
    contextToken = token;
    return;
  }
  if (contextToken === token) return;
  contextToken = token;
  clearLauncherIconCache();
}

export function loadLauncherIconUrl({ assetId, tenantId }) {
  const gen = generation;
  const key = `${launcherIconContextToken(tenantId)}|${assetId}`;
  const cached = entries.get(key);
  if (cached?.url) return Promise.resolve({ url: cached.url, generation: gen });
  if (cached?.promise) return cached.promise;

  const promise = fetchLauncherIconBlob({ assetId, tenantId })
    .then(blob => {
      if (gen !== generation) return { url: '', generation: gen };
      const url = URL.createObjectURL(blob);
      entries.set(key, { url });
      return { url, generation: gen };
    })
    .catch(error => {
      const current = entries.get(key);
      if (current?.promise === promise) entries.delete(key);
      throw error;
    });
  entries.set(key, { promise });
  return promise;
}
